import { errorMessage } from "@streamoid/errors";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { notify } from "../../utils/utils";
import {
  addOrganizationDomainInvite,
  addWorkspaceToOrganization,
  approveJoinRequest,
  cancelOrganizationInvite,
  changeOrganizationUserRole,
  deleteOrganization,
  getOrganization,
  getOrganizationDomainInvites,
  getOrganizationInvites,
  getOrganizationJoinRequests,
  getOrganizationUsers,
  getOrganizationWorkspaces,
  rejectJoinRequest,
  removeOrganizationUser,
  removeWorkspaceFromOrganization,
  revokeOrganizationDomainInvite,
  setWorkspaceOrgVisibility,
  updateOrganization,
} from "../../utils/organizations";
import AddWorkspaceModal from "./AddWorkspaceModal";
import "./style.css";
import { BARE_DOMAIN_MESSAGE, isBareDomain } from "./domain";

const TABS = [
  { id: "workspaces", label: "Workspaces" },
  { id: "members", label: "Members" },
  { id: "domains", label: "Invite domains" },
  { id: "invites", label: "Invites" },
  { id: "join-requests", label: "Join requests" },
];

export default function OrganizationDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const organizationId = params.organizationId as string;

  const [organization, setOrganization] = useState<any>(null);
  const [nameDraft, setNameDraft] = useState("");
  // The organization's company domain and shared/private. Saved together with
  // the name; only what changed is sent.
  const [orgDomainDraft, setOrgDomainDraft] = useState("");
  const [orgTypeDraft, setOrgTypeDraft] = useState("private");
  const [isSavingName, setIsSavingName] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [activeTab, setActiveTab] = useState("workspaces");

  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
  const [showAddWorkspaceModal, setShowAddWorkspaceModal] = useState(false);
  const [removingWorkspaceId, setRemovingWorkspaceId] = useState<
    string | null
  >(null);
  const [savingVisibilityId, setSavingVisibilityId] = useState<string | null>(
    null,
  );

  const [members, setMembers] = useState<any[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [savingMemberId, setSavingMemberId] = useState<string | null>(null);

  const [joinRequests, setJoinRequests] = useState<any[]>([]);
  const [isLoadingJoinRequests, setIsLoadingJoinRequests] = useState(false);
  const [decidingRequestId, setDecidingRequestId] = useState<string | null>(
    null,
  );

  const [invites, setInvites] = useState<any[]>([]);
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);
  const [decidingInviteId, setDecidingInviteId] = useState<string | null>(
    null,
  );

  // Domain claims. An org can only self-serve the domain matching its own
  // admin's email, so extra domains it owns are added from here.
  const [domainInvites, setDomainInvites] = useState<any[]>([]);
  const [isLoadingDomains, setIsLoadingDomains] = useState(false);
  const [domainDraft, setDomainDraft] = useState("");
  const [isAddingDomain, setIsAddingDomain] = useState(false);

  const fetchOrganization = () => {
    getOrganization(organizationId)
      .then((res) => {
        setOrganization(res?.data || null);
        setNameDraft(res?.data?.name || "");
        setOrgDomainDraft(res?.data?.domain || "");
        setOrgTypeDraft(
          res?.data?.domain ? res?.data?.type || "shared" : "private",
        );
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      });
  };

  const fetchWorkspaces = () => {
    setIsLoadingWorkspaces(true);
    getOrganizationWorkspaces(organizationId)
      .then((res) => {
        setWorkspaces(res?.data || []);
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsLoadingWorkspaces(false);
      });
  };

  const fetchMembers = () => {
    setIsLoadingMembers(true);
    getOrganizationUsers(organizationId)
      .then((res) => {
        setMembers(res?.data || []);
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsLoadingMembers(false);
      });
  };

  const fetchJoinRequests = () => {
    setIsLoadingJoinRequests(true);
    getOrganizationJoinRequests(organizationId)
      .then((res) => {
        setJoinRequests(res?.data || []);
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsLoadingJoinRequests(false);
      });
  };

  const fetchInvites = () => {
    setIsLoadingInvites(true);
    getOrganizationInvites(organizationId)
      .then((res) => {
        setInvites(res?.data || []);
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsLoadingInvites(false);
      });
  };

  const fetchDomainInvites = () => {
    setIsLoadingDomains(true);
    getOrganizationDomainInvites(organizationId)
      .then((res) => {
        setDomainInvites(res?.data || []);
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsLoadingDomains(false);
      });
  };

  useEffect(() => {
    if (!organizationId) return;
    fetchOrganization();
    fetchWorkspaces();
    fetchMembers();
    fetchInvites();
    fetchDomainInvites();
    fetchJoinRequests();
  }, [organizationId]);

  const currentType = organization?.domain
    ? organization?.type || "shared"
    : "private";
  const detailChanges = (): Record<string, string> => {
    const changes: Record<string, string> = {};
    const name = nameDraft.trim();
    const domain = orgDomainDraft.trim().toLowerCase();
    const type = domain ? orgTypeDraft : "private";
    if (name !== organization?.name) changes.name = name;
    if (domain !== (organization?.domain || "")) changes.domain = domain;
    // With a domain change, always send the type the form shows: an org
    // without a domain is displayed as private but may be stored as shared,
    // and the backend keeps the stored type when none is sent.
    if (type !== currentType || changes.domain !== undefined) changes.type = type;
    return changes;
  };

  const handleSaveName = () => {
    const name = nameDraft.trim();
    if (!name) {
      notify("Organization name is required", "error");
      return;
    }
    const changes = detailChanges();
    if (changes.domain && !isBareDomain(changes.domain)) {
      notify(BARE_DOMAIN_MESSAGE, "error");
      return;
    }
    if (
      changes.domain === "" &&
      !window.confirm(
        "Remove this organization's domain? It becomes invite-only: no one is matched to it at sign-up, and its admins can no longer create organizations for the company.",
      )
    ) {
      return;
    }

    setIsSavingName(true);
    updateOrganization(organizationId, changes)
      .then(() => {
        notify("Organization updated successfully");
        fetchOrganization();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsSavingName(false);
      });
  };

  const handleDeleteOrganization = () => {
    const confirmed = window.confirm(
      `Delete organization "${organization?.name}"? Its workspaces will be unassigned, not deleted.`,
    );
    if (!confirmed) return;

    setIsDeleting(true);
    deleteOrganization(organizationId)
      .then(() => {
        notify("Organization deleted successfully");
        navigate("/organizations");
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handleAddWorkspace = (workspaceId: string) => {
    return addWorkspaceToOrganization(organizationId, workspaceId)
      .then(() => {
        notify("Workspace added to organization");
        fetchWorkspaces();
        fetchOrganization();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      });
  };

  const handleRemoveWorkspace = (workspace: any) => {
    const confirmed = window.confirm(
      `Remove workspace "${workspace.name}" from this organization?`,
    );
    if (!confirmed) return;

    setRemovingWorkspaceId(workspace._id);
    removeWorkspaceFromOrganization(organizationId, workspace._id)
      .then(() => {
        notify("Workspace removed from organization");
        fetchWorkspaces();
        fetchOrganization();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setRemovingWorkspaceId(null);
      });
  };

  const handleChangeVisibility = (workspace: any, next: string) => {
    const current = workspace.orgVisibility || "private";
    if (next === current) return;

    // Shared only lists the workspace in the org's workspace picker, so members
    // can join it; it grants no access by itself. Private takes it off the
    // picker, so it is invite-only — say who keeps access, since it isn't nobody.
    const confirmed = window.confirm(
      next === "shared"
        ? `Share "${workspace.name}" with ${organization?.name}?\n\nIt will be listed in the organization's workspace picker, so members can join it.`
        : `Make "${workspace.name}" private?\n\nIt will be removed from the organization's workspace picker, so it is invite-only. Its current members and organization admins keep access.`,
    );
    if (!confirmed) return;

    setSavingVisibilityId(workspace._id);
    setWorkspaceOrgVisibility(organizationId, workspace._id, next)
      .then(() => {
        notify(
          next === "shared"
            ? "Workspace is now listed in the organization's workspace picker"
            : "Workspace is now private",
        );
        fetchWorkspaces();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setSavingVisibilityId(null);
      });
  };

  const handleChangeMemberRole = (member: any, role: string) => {
    setSavingMemberId(member._id);
    changeOrganizationUserRole(organizationId, {
      userId: member.userId?._id || member.userId,
      role,
    })
      .then(() => {
        notify("Role updated successfully");
        fetchMembers();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setSavingMemberId(null);
      });
  };

  const handleRemoveMember = (member: any) => {
    const email = member.user?.email || "this member";
    const confirmed = window.confirm(
      `Remove ${email} from the organization?\n\nThey will also be removed from every workspace in this organization.`,
    );
    if (!confirmed) return;

    setSavingMemberId(member._id);
    removeOrganizationUser(organizationId, {
      userIds: [member.userId?._id || member.userId],
    })
      .then(() => {
        notify("Member removed successfully");
        fetchMembers();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setSavingMemberId(null);
      });
  };

  const handleCancelInvite = (invite: any) => {
    const confirmed = window.confirm(
      `Cancel the pending invite for "${invite.email}"?`,
    );
    if (!confirmed) return;

    setDecidingInviteId(invite._id);
    cancelOrganizationInvite(invite.token)
      .then(() => {
        notify("Invite cancelled");
        fetchInvites();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setDecidingInviteId(null);
      });
  };

  const handleRevokeDomainInvite = (invite: any) => {
    const confirmed = window.confirm(
      `Remove "${invite.domain}" from this organization's invite domains?\n\nPeople invited from @${invite.domain} from now on join as guests, unless it is the organization's own domain. Members who already joined keep their access.`,
    );
    if (!confirmed) return;

    setDecidingInviteId(invite._id);
    revokeOrganizationDomainInvite(organizationId, invite._id)
      .then(() => {
        notify("Domain invite revoked");
        fetchInvites();
        fetchDomainInvites();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setDecidingInviteId(null);
      });
  };

  const handleAddDomain = () => {
    const domain = domainDraft.trim().toLowerCase();
    if (!domain) {
      notify("Domain is required", "error");
      return;
    }

    // An invite domain decides the org role of people invited from it
    // (member, not guest). It doesn't drive sign-up matching any more: that
    // uses the organization's own domain.
    const confirmed = window.confirm(
      `Add "${domain}" to ${organization?.name}'s invite domains?\n\nPeople invited with an @${domain} email address will join this organization as members instead of guests. Only do this once you have verified the organization owns the domain.`,
    );
    if (!confirmed) return;

    setIsAddingDomain(true);
    addOrganizationDomainInvite(organizationId, {
      domain,
      autoJoinRole: "member",
    })
      .then(() => {
        notify("Domain added successfully");
        setDomainDraft("");
        fetchDomainInvites();
        fetchInvites();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsAddingDomain(false);
      });
  };

  const handleApproveJoinRequest = (requestId: string) => {
    setDecidingRequestId(requestId);
    approveJoinRequest(requestId)
      .then(() => {
        notify("Join request approved");
        fetchJoinRequests();
        fetchWorkspaces();
        fetchOrganization();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setDecidingRequestId(null);
      });
  };

  const handleRejectJoinRequest = (requestId: string) => {
    setDecidingRequestId(requestId);
    rejectJoinRequest(requestId)
      .then(() => {
        notify("Join request rejected");
        fetchJoinRequests();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setDecidingRequestId(null);
      });
  };

  if (!organization) {
    return (
      <div className="organizations-page">
        <div className="dashboard-settings-section">
          <div className="version-subtitle">Loading organization...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="organizations-page">
      <div className="dashboard-settings-section">
        <div className="organization-detail-back" onClick={() => navigate("/organizations")}>
          ← All organizations
        </div>

        <div className="settings-container organization-detail-header">
          <div className="version-header">
            <div className="version-title">Organization details</div>
            <div className="version-subtitle">
              Created by {organization.addedBy?.email || "-"} ·{" "}
              {organization.rootDomain
                ? `Company: ${organization.rootDomain}`
                : "No domain: invite-only"}
            </div>
          </div>
          <div className="organization-fields">
            <label className="organization-field">
              <span>Name</span>
              <input
                className="version-input"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                disabled={isSavingName}
              />
            </label>
            <label className="organization-field">
              <span>Company domain</span>
              <input
                className="version-input"
                value={orgDomainDraft}
                placeholder="e.g. acme.com"
                onChange={(e) => setOrgDomainDraft(e.target.value)}
                disabled={isSavingName}
              />
            </label>
            <label className="organization-field">
              <span>Who can join</span>
              <select
                className="version-input"
                value={orgDomainDraft.trim() ? orgTypeDraft : "private"}
                onChange={(e) => setOrgTypeDraft(e.target.value)}
                disabled={isSavingName || !orgDomainDraft.trim()}
              >
                <option value="shared">Shared</option>
                <option value="private">Private</option>
              </select>
            </label>
          </div>
          <div className="organization-field-hint">
            {!orgDomainDraft.trim()
              ? "No domain: the organization is invite-only, and its admins can't create organizations for a company."
              : (orgDomainDraft.trim() ? orgTypeDraft : "private") === "shared"
                ? "Shared: new users with an email on this company's domain can join it at sign-up."
                : "Private: only people invited to one of its workspaces can join."}
          </div>
          <div className="version-controls organization-actions">
            <button
              className="version-save-btn"
              onClick={handleSaveName}
              disabled={isSavingName || Object.keys(detailChanges()).length === 0}
            >
              {isSavingName ? "Saving..." : "Save"}
            </button>
            <button
              className="user-specialization-delete-btn"
              onClick={handleDeleteOrganization}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete organization"}
            </button>
          </div>
        </div>

        <div className="organization-detail-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`organization-detail-tab ${
                activeTab === tab.id ? "organization-detail-tab-active" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "workspaces" && (
          <div className="settings-container organization-detail-panel">
            <div className="version-controls">
              <div className="version-title">Workspaces in this organization</div>
              <button
                className="version-save-btn"
                onClick={() => setShowAddWorkspaceModal(true)}
              >
                + Add existing workspace
              </button>
            </div>

            {isLoadingWorkspaces ? (
              <div className="version-subtitle">Loading...</div>
            ) : workspaces.length === 0 ? (
              <div className="version-subtitle">
                No workspaces in this organization yet.
              </div>
            ) : (
              <div className="organizations-table-scroll">
                <table className="organizations-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Owner</th>
                      <th>Visibility</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {workspaces.map((ws) => (
                      <tr key={ws._id}>
                        <td>{ws.name}</td>
                        <td>{ws.addedBy?.email || "-"}</td>
                        <td>
                          {/* Absent means the workspace predates the field, and
                              the backend only lists it in the org's workspace
                              picker when the stored value is literally
                              "shared" — so it reads as private, and switching
                              to Shared is a real change rather than a no-op. */}
                          <select
                            className="version-input organizations-role-select"
                            value={ws.orgVisibility || "private"}
                            disabled={savingVisibilityId === ws._id}
                            onChange={(e) =>
                              handleChangeVisibility(ws, e.target.value)
                            }
                          >
                            <option value="private">private</option>
                            <option value="shared">shared</option>
                          </select>
                        </td>
                        <td>
                          <button
                            className="user-specialization-delete-btn"
                            onClick={() => handleRemoveWorkspace(ws)}
                            disabled={
                              removingWorkspaceId === ws._id ||
                              savingVisibilityId === ws._id
                            }
                          >
                            {removingWorkspaceId === ws._id
                              ? "Removing..."
                              : "Remove"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "members" && (
          <div className="settings-container organization-detail-panel">
            <div className="version-title">Members</div>

            {isLoadingMembers ? (
              <div className="version-subtitle">Loading...</div>
            ) : members.length === 0 ? (
              <div className="version-subtitle">No members yet.</div>
            ) : (
              <div className="organizations-table-scroll">
                <table className="organizations-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr key={member._id}>
                        <td>{member.user?.name || "-"}</td>
                        <td>{member.user?.email || "-"}</td>
                        <td>
                          <select
                            className="version-input organizations-role-select"
                            value={member.role}
                            disabled={savingMemberId === member._id}
                            onChange={(e) =>
                              handleChangeMemberRole(member, e.target.value)
                            }
                          >
                            <option value="admin">admin</option>
                            <option value="member">member</option>
                          </select>
                        </td>
                        <td>
                          <button
                            className="user-specialization-delete-btn"
                            onClick={() => handleRemoveMember(member)}
                            disabled={savingMemberId === member._id}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "domains" && (
          <div className="settings-container organization-detail-panel">
            <div className="version-header">
              <div className="version-title">Invite domains</div>
              <div className="version-subtitle">
                People invited with an email on one of these domains join this
                organization as members instead of guests. Sign-up matching
                doesn't use this list: it uses the organization's domain above.
                Add only domains the organization owns.
              </div>
            </div>

            <div className="version-controls organization-domain-form">
              <input
                className="version-input"
                placeholder="acme.co.uk"
                value={domainDraft}
                onChange={(e) => setDomainDraft(e.target.value)}
                disabled={isAddingDomain}
              />
              <button
                className="version-save-btn"
                onClick={handleAddDomain}
                disabled={isAddingDomain || !domainDraft.trim()}
              >
                {isAddingDomain ? "Adding..." : "+ Add domain"}
              </button>
            </div>

            {isLoadingDomains ? (
              <div className="version-subtitle">Loading...</div>
            ) : domainInvites.length === 0 ? (
              <div className="version-subtitle">
                No domains claimed by this organization yet.
              </div>
            ) : (
              <div className="organizations-table-scroll">
                <table className="organizations-table">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Auto-join role</th>
                      <th>Status</th>
                      <th>Added by</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {domainInvites.map((invite) => (
                      <tr key={invite._id}>
                        <td>{invite.domain}</td>
                        {/* Domain users always auto-join as members. */}
                        <td>member</td>
                        <td>
                          <span
                            className={`organizations-status-badge organizations-status-${
                              invite.status === "active" ? "active" : "inactive"
                            }`}
                          >
                            {invite.status}
                          </span>
                        </td>
                        <td>
                          {invite.invitedByUser?.email ||
                            invite.invitedByUser?.name ||
                            "-"}
                        </td>
                        <td>
                          {invite.status === "active" && (
                            <button
                              className="user-specialization-delete-btn"
                              onClick={() => handleRevokeDomainInvite(invite)}
                              disabled={decidingInviteId === invite._id}
                            >
                              {decidingInviteId === invite._id
                                ? "Revoking..."
                                : "Revoke"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "invites" && (
          <div className="settings-container organization-detail-panel">
            <div className="version-header">
              <div className="version-title">Invites</div>
              <div className="version-subtitle">
                Email and domain invites sent by org admins/members to join
                this organization.
              </div>
            </div>

            {isLoadingInvites ? (
              <div className="version-subtitle">Loading...</div>
            ) : invites.length === 0 ? (
              <div className="version-subtitle">No invites yet.</div>
            ) : (
              <div className="organizations-table-scroll">
                <table className="organizations-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Recipient</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Invited by</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.map((invite) => (
                      <tr key={invite._id}>
                        <td>{invite.kind === "domain" ? "Domain" : "Email"}</td>
                        <td>{invite.kind === "domain" ? invite.domain : invite.email}</td>
                        <td>{invite.kind === "domain" ? "member" : invite.role}</td>
                        <td>
                          <span
                            className={`organizations-status-badge organizations-status-${
                              invite.status === "active" ? "active" : "inactive"
                            }`}
                          >
                            {invite.status}
                          </span>
                        </td>
                        <td>
                          {typeof invite.invitedBy === "object"
                            ? invite.invitedBy?.email || invite.invitedBy?.name || "-"
                            : "-"}
                        </td>
                        <td>
                          {invite.status === "active" && invite.kind === "email" && (
                            <button
                              className="user-specialization-delete-btn"
                              onClick={() => handleCancelInvite(invite)}
                              disabled={decidingInviteId === invite._id}
                            >
                              Cancel
                            </button>
                          )}
                          {invite.status === "active" && invite.kind === "domain" && (
                            <button
                              className="user-specialization-delete-btn"
                              onClick={() => handleRevokeDomainInvite(invite)}
                              disabled={decidingInviteId === invite._id}
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "join-requests" && (
          <div className="settings-container organization-detail-panel">
            <div className="version-header">
              <div className="version-title">Pending join requests</div>
              <div className="version-subtitle">
                Raised by a workspace admin asking to join this organization.
              </div>
            </div>

            {isLoadingJoinRequests ? (
              <div className="version-subtitle">Loading...</div>
            ) : joinRequests.length === 0 ? (
              <div className="version-subtitle">No pending join requests.</div>
            ) : (
              <div className="organizations-table-scroll">
                <table className="organizations-table">
                  <thead>
                    <tr>
                      <th>Workspace</th>
                      <th>Requested by</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {joinRequests.map((request) => (
                      <tr key={request._id}>
                        <td>{request.workspace?.name || "-"}</td>
                        <td>{request.requester?.email || "-"}</td>
                        <td className="organization-detail-join-request-actions">
                          <button
                            className="version-save-btn"
                            onClick={() => handleApproveJoinRequest(request._id)}
                            disabled={decidingRequestId === request._id}
                          >
                            Approve
                          </button>
                          <button
                            className="user-specialization-delete-btn"
                            onClick={() => handleRejectJoinRequest(request._id)}
                            disabled={decidingRequestId === request._id}
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {showAddWorkspaceModal && (
        <AddWorkspaceModal
          organizationId={organizationId}
          onClose={() => setShowAddWorkspaceModal(false)}
          onAdd={handleAddWorkspace}
        />
      )}
    </div>
  );
}
